import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './Packages.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * Packages - Complete Dynamic Package Management System
 * Supports all 5 package types with full feature configuration
 */
function Packages() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [activeTab, setActiveTab] = useState('basic'); // Tab state for modal
    const [submitting, setSubmitting] = useState(false); // Loading state for submit button

    // Complete formData structure with NEW schema
    const [formData, setFormData] = useState({
        packageType: '',
        name: '',
        nameBangla: '',
        description: '',
        isActive: true,
        isVisible: true,
        sortOrder: 0,

        pricing: {
            trial: { duration: 7, price: 0 },
            monthly: { price: 0, displayPrice: 0, period: 'month', billingCycle: 30 },
            yearly: { price: 0, displayPrice: 0, period: 'year', billingCycle: 365, discountPercentage: 0 },
            quarterly: { enabled: false, price: 0, billingCycle: 90 }
        },

        limits: {
            visitors: -1,
            products: -1,
            orders: -1,
            teamMembers: 1,
            domains: 1,
            storage: -1,
            bandwidth: -1
        },

        features: {
            // Core Features
            customDomain: false,
            subDomain: true,
            // E-commerce Features
            bkashIntegration: false,
            sslIntegration: false,
            courierIntegration: false,
            inventoryManagement: true,
            multiCurrency: false,
            // Marketing Features
            pixelSetup: true,
            facebookConversionsAPI: true,
            googleAnalytics: true,
            googleTagManager: true,
            seoTools: false,
            // Communication Features
            smsNotifications: false,
            emailNotifications: true,
            whatsappIntegration: true,
            messengerIntegration: true,
            // Support Features
            emailSupport: true,
            phoneSupport: false,
            prioritySupport: false,
            dedicatedAccountManager: false,
            // Analytics & Reports
            basicReports: true,
            advancedReports: false,
            customerAnalytics: false,
            // Performance
            superFast: false,
            // Customization
            customThemes: false,
            landingPageBuilder: true,
            // Advanced Features
            apiAccess: false,
            webhooks: false,
            customDevelopment: false
        },

        display: {
            colors: { header: 'rgb(66, 82, 206)', price: 'rgb(64, 78, 189)', button: '#4252ce' },
            badge: { text: '', color: '' },
            icon: '',
            highlightFeatures: []
        },

        settings: {
            trialDuration: 7,
            gracePeriod: 7,
            allowDowngrade: true,
            allowUpgrade: true,
            autoRenewal: false,
            proRateOnUpgrade: false,
            refundPolicy: 'none',
            minimumCommitment: 0
        },

        discounts: {
            firstTime: { enabled: false, percentage: 0, description: '' },
            renewal: { enabled: false, percentage: 0, minRenewals: 0 },
            loyalty: { enabled: false, percentage: 0, minMonths: 0 },
            referral: { enabled: false, percentage: 0 }
        }
    });

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${BASEURL}packages`, {
                headers: { token }
            });

            if (response.data.success) {
                setPackages(response.data.data);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching packages:', err);
            setError('Failed to load packages');
            setLoading(false);
        }
    };

    const handleDelete = async (packageId, packageName) => {
        if (!window.confirm(`Delete package: ${packageName}?\n\nThis action cannot be undone.`)) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${BASEURL}package/delete/${packageId}`, {
                headers: { token }
            });
            alert('Package deleted successfully');
            fetchPackages();
        } catch (err) {
            console.error('Delete error:', err);
            alert(err.response?.data?.error || 'Failed to delete package');
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setError('');
        setSubmitting(false);
    };

    const resetFormData = () => {
        setFormData({
            packageType: '',
            name: '',
            nameBangla: '',
            description: '',
            isActive: true,
            isVisible: true,
            sortOrder: 0,
            pricing: {
                trial: { duration: 7, price: 0 },
                monthly: { price: 0, displayPrice: 0, period: 'month', billingCycle: 30 },
                yearly: { price: 0, displayPrice: 0, period: 'year', billingCycle: 365, discountPercentage: 0 },
                quarterly: { enabled: false, price: 0, billingCycle: 90 }
            },
            limits: {
                visitors: -1,
                products: -1,
                orders: -1,
                teamMembers: 1,
                domains: 1,
                storage: -1,
                bandwidth: -1
            },
            features: {
                customDomain: false,
                subDomain: true,
                bkashIntegration: false,
                sslIntegration: false,
                courierIntegration: false,
                inventoryManagement: true,
                multiCurrency: false,
                pixelSetup: true,
                facebookConversionsAPI: true,
                googleAnalytics: true,
                googleTagManager: true,
                seoTools: false,
                smsNotifications: false,
                emailNotifications: true,
                whatsappIntegration: true,
                messengerIntegration: true,
                emailSupport: true,
                phoneSupport: false,
                prioritySupport: false,
                dedicatedAccountManager: false,
                basicReports: true,
                advancedReports: false,
                customerAnalytics: false,
                superFast: false,
                customThemes: false,
                landingPageBuilder: true,
                apiAccess: false,
                webhooks: false,
                customDevelopment: false
            },
            display: {
                colors: { header: 'rgb(66, 82, 206)', price: 'rgb(64, 78, 189)', button: '#4252ce' },
                badge: { text: '', color: '' },
                icon: '',
                highlightFeatures: []
            },
            settings: {
                trialDuration: 7,
                gracePeriod: 7,
                allowDowngrade: true,
                allowUpgrade: true,
                autoRenewal: false,
                proRateOnUpgrade: false,
                refundPolicy: 'none',
                minimumCommitment: 0
            },
            discounts: {
                firstTime: { enabled: false, percentage: 0, description: '' },
                renewal: { enabled: false, percentage: 0, minRenewals: 0 },
                loyalty: { enabled: false, percentage: 0, minMonths: 0 },
                referral: { enabled: false, percentage: 0 }
            }
        });
    };

    const openCreateModal = () => {
        setEditingPackage(null);
        resetFormData();
        setActiveTab('basic');
        setError('');
        setShowModal(true);
    };

    const openEditModal = (pkg) => {
        setEditingPackage(pkg);
        setFormData({
            packageType: pkg.packageType || '',
            name: pkg.name || '',
            nameBangla: pkg.nameBangla || '',
            description: pkg.description || '',
            isActive: pkg.isActive !== undefined ? pkg.isActive : true,
            isVisible: pkg.isVisible !== undefined ? pkg.isVisible : true,
            sortOrder: pkg.sortOrder || 0,
            pricing: pkg.pricing || formData.pricing,
            limits: pkg.limits || formData.limits,
            features: pkg.features || formData.features,
            display: pkg.display || formData.display,
            settings: pkg.settings || formData.settings,
            discounts: pkg.discounts || formData.discounts
        });
        setActiveTab('basic');
        setError('');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!formData.packageType || !formData.name) {
            setError('Package Type and Name are required');
            return;
        }

        if (!formData.pricing.monthly.price || !formData.pricing.yearly.price) {
            setError('Monthly and Yearly pricing are required');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const token = localStorage.getItem('token');

            if (editingPackage) {
                await axios.put(
                    `${BASEURL}package/update/${editingPackage._id}`,
                    formData,
                    { headers: { token } }
                );
            } else {
                await axios.post(
                    `${BASEURL}package/create`,
                    formData,
                    { headers: { token } }
                );
            }

            closeModal();
            fetchPackages();
        } catch (err) {
            console.error('Submit error:', err);
            setError(err.response?.data?.error || `Failed to ${editingPackage ? 'update' : 'create'} package`);
        } finally {
            setSubmitting(false);
        }
    };

    // Helper to update nested state
    const updateNestedField = (path, value) => {
        const keys = path.split('.');
        setFormData(prev => {
            const updated = { ...prev };
            let current = updated;
            for (let i = 0; i < keys.length - 1; i++) {
                current[keys[i]] = { ...current[keys[i]] };
                current = current[keys[i]];
            }
            current[keys[keys.length - 1]] = value;
            return updated;
        });
    };

    // Count enabled features
    const countEnabledFeatures = (features) => {
        if (!features) return 0;
        return Object.values(features).filter(v => v === true).length;
    };

    if (loading) {
        return (
            <Layout>
                <div className={styles.loading}>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading packages...
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        <h1>Package Management</h1>
                        <p>Create and manage subscription packages dynamically</p>
                    </div>
                    <button className={styles.createBtn} onClick={openCreateModal}>
                        <i className="fas fa-plus"></i>
                        Create Package
                    </button>
                </div>

                {error && (
                    <div className={styles.error}>
                        <i className="fas fa-exclamation-circle"></i>
                        {error}
                    </div>
                )}

                {/* Packages Grid */}
                <div className={styles.packagesGrid}>
                    {packages.map((pkg) => (
                        <div key={pkg._id} className={styles.packageCard}>
                            <div className={styles.packageHeader}>
                                <h3>{pkg.name}</h3>
                                <span className={`${styles.packageType} ${styles[pkg.packageType]}`}>
                                    {pkg.packageType}
                                </span>
                            </div>

                            <div className={styles.pricing}>
                                <div className={styles.priceItem}>
                                    <label>Monthly:</label>
                                    <strong>৳{pkg.pricing?.monthly?.price || 0}</strong>
                                </div>
                                <div className={styles.priceItem}>
                                    <label>Yearly:</label>
                                    <strong>৳{pkg.pricing?.yearly?.price || 0}</strong>
                                    {pkg.pricing?.yearly?.discountPercentage > 0 && (
                                        <span className={styles.discount}>
                                            -{pkg.pricing.yearly.discountPercentage}%
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className={styles.limits}>
                                <h4>Limits:</h4>
                                <ul>
                                    <li>
                                        <i className="fas fa-users"></i>
                                        Visitors: {pkg.limits?.visitors === -1 ? 'Unlimited' : pkg.limits?.visitors || 0}
                                    </li>
                                    <li>
                                        <i className="fas fa-box"></i>
                                        Products: {pkg.limits?.products === -1 ? 'Unlimited' : pkg.limits?.products || 0}
                                    </li>
                                    <li>
                                        <i className="fas fa-shopping-cart"></i>
                                        Orders: {pkg.limits?.orders === -1 ? 'Unlimited' : pkg.limits?.orders || 0}
                                    </li>
                                </ul>
                            </div>

                            <div className={styles.featuresCount}>
                                <i className="fas fa-check-circle"></i>
                                {countEnabledFeatures(pkg.features)}/29 features enabled
                            </div>

                            <div className={styles.statusBadges}>
                                {pkg.isActive && <span className={styles.badgeActive}>Active</span>}
                                {pkg.isVisible && <span className={styles.badgeVisible}>Visible</span>}
                            </div>

                            <div className={styles.cardActions}>
                                <button
                                    className={styles.editCardBtn}
                                    onClick={() => openEditModal(pkg)}
                                >
                                    <i className="fas fa-edit"></i>
                                    Edit
                                </button>
                                <button
                                    className={styles.deleteCardBtn}
                                    onClick={() => handleDelete(pkg._id, pkg.name)}
                                >
                                    <i className="fas fa-trash"></i>
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}

                    {packages.length === 0 && (
                        <div className={styles.noPackages}>
                            <i className="fas fa-box-open"></i>
                            <p>No packages found</p>
                            <button onClick={openCreateModal}>Create First Package</button>
                        </div>
                    )}
                </div>

                {/* Modal */}
                {showModal && (
                    <div className={styles.modal} onClick={closeModal}>
                        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <h2>{editingPackage ? 'Edit Package' : 'Create Package'}</h2>
                                <button onClick={closeModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className={styles.tabs}>
                                <button
                                    className={activeTab === 'basic' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('basic')}
                                >
                                    Basic Info
                                </button>
                                <button
                                    className={activeTab === 'pricing' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('pricing')}
                                >
                                    Pricing
                                </button>
                                <button
                                    className={activeTab === 'limits' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('limits')}
                                >
                                    Limits
                                </button>
                                <button
                                    className={activeTab === 'features' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('features')}
                                >
                                    Features
                                </button>
                                <button
                                    className={activeTab === 'settings' ? styles.activeTab : ''}
                                    onClick={() => setActiveTab('settings')}
                                >
                                    Settings
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                {/* Error Display */}
                                {error && (
                                    <div className={styles.modalError}>
                                        <i className="fas fa-exclamation-circle"></i>
                                        {error}
                                    </div>
                                )}

                                {/* Basic Info Tab */}
                                {activeTab === 'basic' && (
                                    <div className={styles.tabContent}>
                                        <div className={styles.formGroup}>
                                            <label>Package Type* <small>(Choose from 5 types)</small></label>
                                            <select
                                                value={formData.packageType}
                                                onChange={(e) => setFormData({ ...formData, packageType: e.target.value })}
                                                required
                                                disabled={editingPackage} // Can't change type when editing
                                            >
                                                <option value="">Select package type...</option>
                                                <option value="startup">Startup</option>
                                                <option value="smallBusiness">Small Business</option>
                                                <option value="business">Business</option>
                                                <option value="enterprise">Enterprise</option>
                                                <option value="custom">Custom</option>
                                            </select>
                                            {editingPackage && <small>Package type cannot be changed after creation</small>}
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Package Name*</label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="e.g., Business Plan"
                                                required
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Package Name (Bangla)</label>
                                            <input
                                                type="text"
                                                value={formData.nameBangla}
                                                onChange={(e) => setFormData({ ...formData, nameBangla: e.target.value })}
                                                placeholder="e.g., বিজনেস প্যাকেজ"
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Description</label>
                                            <textarea
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Package description..."
                                                rows="3"
                                            />
                                        </div>

                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.isActive}
                                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                    />
                                                    Active (can be subscribed to)
                                                </label>
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.isVisible}
                                                        onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                                                    />
                                                    Visible (shown on pricing page)
                                                </label>
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Sort Order <small>(for display ordering)</small></label>
                                            <input
                                                type="number"
                                                value={formData.sortOrder}
                                                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Pricing Tab */}
                                {activeTab === 'pricing' && (
                                    <div className={styles.tabContent}>
                                        <h3>Monthly Pricing</h3>
                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Price (৳)*</label>
                                                <input
                                                    type="number"
                                                    value={formData.pricing.monthly.price}
                                                    onChange={(e) => updateNestedField('pricing.monthly.price', parseInt(e.target.value) || 0)}
                                                    required
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Billing Cycle (days)</label>
                                                <input
                                                    type="number"
                                                    value={formData.pricing.monthly.billingCycle}
                                                    onChange={(e) => updateNestedField('pricing.monthly.billingCycle', parseInt(e.target.value) || 30)}
                                                />
                                            </div>
                                        </div>

                                        <h3>Yearly Pricing</h3>
                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Price (৳)*</label>
                                                <input
                                                    type="number"
                                                    value={formData.pricing.yearly.price}
                                                    onChange={(e) => updateNestedField('pricing.yearly.price', parseInt(e.target.value) || 0)}
                                                    required
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Discount %</label>
                                                <input
                                                    type="number"
                                                    value={formData.pricing.yearly.discountPercentage}
                                                    onChange={(e) => updateNestedField('pricing.yearly.discountPercentage', parseInt(e.target.value) || 0)}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Billing Cycle (days)</label>
                                                <input
                                                    type="number"
                                                    value={formData.pricing.yearly.billingCycle}
                                                    onChange={(e) => updateNestedField('pricing.yearly.billingCycle', parseInt(e.target.value) || 365)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Limits Tab */}
                                {activeTab === 'limits' && (
                                    <div className={styles.tabContent}>
                                        <p className={styles.helpText}>Set -1 for unlimited. Set specific number for limits.</p>

                                        <div className={styles.formGroup}>
                                            <label>Visitors per Month</label>
                                            <div className={styles.limitInput}>
                                                <input
                                                    type="number"
                                                    value={formData.limits.visitors}
                                                    onChange={(e) => updateNestedField('limits.visitors', parseInt(e.target.value))}
                                                />
                                                <button
                                                    type="button"
                                                    className={formData.limits.visitors === -1 ? styles.unlimitedActive : ''}
                                                    onClick={() => updateNestedField('limits.visitors', formData.limits.visitors === -1 ? 0 : -1)}
                                                >
                                                    <i className="fas fa-infinity"></i>
                                                    Unlimited
                                                </button>
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Products</label>
                                            <div className={styles.limitInput}>
                                                <input
                                                    type="number"
                                                    value={formData.limits.products}
                                                    onChange={(e) => updateNestedField('limits.products', parseInt(e.target.value))}
                                                />
                                                <button
                                                    type="button"
                                                    className={formData.limits.products === -1 ? styles.unlimitedActive : ''}
                                                    onClick={() => updateNestedField('limits.products', formData.limits.products === -1 ? 0 : -1)}
                                                >
                                                    <i className="fas fa-infinity"></i>
                                                    Unlimited
                                                </button>
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Orders per Month</label>
                                            <div className={styles.limitInput}>
                                                <input
                                                    type="number"
                                                    value={formData.limits.orders}
                                                    onChange={(e) => updateNestedField('limits.orders', parseInt(e.target.value))}
                                                />
                                                <button
                                                    type="button"
                                                    className={formData.limits.orders === -1 ? styles.unlimitedActive : ''}
                                                    onClick={() => updateNestedField('limits.orders', formData.limits.orders === -1 ? 0 : -1)}
                                                >
                                                    <i className="fas fa-infinity"></i>
                                                    Unlimited
                                                </button>
                                            </div>
                                        </div>

                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Team Members</label>
                                                <input
                                                    type="number"
                                                    value={formData.limits.teamMembers}
                                                    onChange={(e) => updateNestedField('limits.teamMembers', parseInt(e.target.value) || 1)}
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Custom Domains</label>
                                                <input
                                                    type="number"
                                                    value={formData.limits.domains}
                                                    onChange={(e) => updateNestedField('limits.domains', parseInt(e.target.value) || 1)}
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Storage (GB, -1 = unlimited)</label>
                                                <input
                                                    type="number"
                                                    value={formData.limits.storage}
                                                    onChange={(e) => updateNestedField('limits.storage', parseInt(e.target.value))}
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Bandwidth (GB/mo, -1 = unlimited)</label>
                                                <input
                                                    type="number"
                                                    value={formData.limits.bandwidth}
                                                    onChange={(e) => updateNestedField('limits.bandwidth', parseInt(e.target.value))}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Features Tab */}
                                {activeTab === 'features' && (
                                    <div className={styles.tabContent}>
                                        <p className={styles.helpText}>Enable or disable features for this package</p>

                                        <div className={styles.featureSection}>
                                            <h3><i className="fas fa-star"></i> Core Features</h3>
                                            <div className={styles.featureGrid}>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.customDomain}
                                                        onChange={(e) => updateNestedField('features.customDomain', e.target.checked)}
                                                    />
                                                    <span>Custom Domain</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.subDomain}
                                                        onChange={(e) => updateNestedField('features.subDomain', e.target.checked)}
                                                    />
                                                    <span>Sub Domain</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className={styles.featureSection}>
                                            <h3><i className="fas fa-shopping-bag"></i> E-commerce Features</h3>
                                            <div className={styles.featureGrid}>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.bkashIntegration}
                                                        onChange={(e) => updateNestedField('features.bkashIntegration', e.target.checked)}
                                                    />
                                                    <span>bKash Integration</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.sslIntegration}
                                                        onChange={(e) => updateNestedField('features.sslIntegration', e.target.checked)}
                                                    />
                                                    <span>SSL Integration</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.courierIntegration}
                                                        onChange={(e) => updateNestedField('features.courierIntegration', e.target.checked)}
                                                    />
                                                    <span>Courier Integration</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.inventoryManagement}
                                                        onChange={(e) => updateNestedField('features.inventoryManagement', e.target.checked)}
                                                    />
                                                    <span>Inventory Management</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.multiCurrency}
                                                        onChange={(e) => updateNestedField('features.multiCurrency', e.target.checked)}
                                                    />
                                                    <span>Multi Currency</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className={styles.featureSection}>
                                            <h3><i className="fas fa-bullhorn"></i> Marketing Features</h3>
                                            <div className={styles.featureGrid}>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.pixelSetup}
                                                        onChange={(e) => updateNestedField('features.pixelSetup', e.target.checked)}
                                                    />
                                                    <span>Facebook Pixel Setup</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.facebookConversionsAPI}
                                                        onChange={(e) => updateNestedField('features.facebookConversionsAPI', e.target.checked)}
                                                    />
                                                    <span>FB Conversions API</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.googleAnalytics}
                                                        onChange={(e) => updateNestedField('features.googleAnalytics', e.target.checked)}
                                                    />
                                                    <span>Google Analytics</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.googleTagManager}
                                                        onChange={(e) => updateNestedField('features.googleTagManager', e.target.checked)}
                                                    />
                                                    <span>Google Tag Manager</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.seoTools}
                                                        onChange={(e) => updateNestedField('features.seoTools', e.target.checked)}
                                                    />
                                                    <span>SEO Tools</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className={styles.featureSection}>
                                            <h3><i className="fas fa-comments"></i> Communication Features</h3>
                                            <div className={styles.featureGrid}>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.smsNotifications}
                                                        onChange={(e) => updateNestedField('features.smsNotifications', e.target.checked)}
                                                    />
                                                    <span>SMS Notifications</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.emailNotifications}
                                                        onChange={(e) => updateNestedField('features.emailNotifications', e.target.checked)}
                                                    />
                                                    <span>Email Notifications</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.whatsappIntegration}
                                                        onChange={(e) => updateNestedField('features.whatsappIntegration', e.target.checked)}
                                                    />
                                                    <span>WhatsApp Button</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.messengerIntegration}
                                                        onChange={(e) => updateNestedField('features.messengerIntegration', e.target.checked)}
                                                    />
                                                    <span>Messenger Button</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className={styles.featureSection}>
                                            <h3><i className="fas fa-headset"></i> Support Features</h3>
                                            <div className={styles.featureGrid}>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.emailSupport}
                                                        onChange={(e) => updateNestedField('features.emailSupport', e.target.checked)}
                                                    />
                                                    <span>Email Support</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.phoneSupport}
                                                        onChange={(e) => updateNestedField('features.phoneSupport', e.target.checked)}
                                                    />
                                                    <span>Phone Support</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.prioritySupport}
                                                        onChange={(e) => updateNestedField('features.prioritySupport', e.target.checked)}
                                                    />
                                                    <span>Priority Support</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.dedicatedAccountManager}
                                                        onChange={(e) => updateNestedField('features.dedicatedAccountManager', e.target.checked)}
                                                    />
                                                    <span>Dedicated Account Manager</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className={styles.featureSection}>
                                            <h3><i className="fas fa-chart-line"></i> Analytics & Reports</h3>
                                            <div className={styles.featureGrid}>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.basicReports}
                                                        onChange={(e) => updateNestedField('features.basicReports', e.target.checked)}
                                                    />
                                                    <span>Basic Reports</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.advancedReports}
                                                        onChange={(e) => updateNestedField('features.advancedReports', e.target.checked)}
                                                    />
                                                    <span>Advanced Reports</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.customerAnalytics}
                                                        onChange={(e) => updateNestedField('features.customerAnalytics', e.target.checked)}
                                                    />
                                                    <span>Customer Analytics</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className={styles.featureSection}>
                                            <h3><i className="fas fa-rocket"></i> Performance & Customization</h3>
                                            <div className={styles.featureGrid}>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.superFast}
                                                        onChange={(e) => updateNestedField('features.superFast', e.target.checked)}
                                                    />
                                                    <span>Super Fast (CDN)</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.customThemes}
                                                        onChange={(e) => updateNestedField('features.customThemes', e.target.checked)}
                                                    />
                                                    <span>Custom Themes</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.landingPageBuilder}
                                                        onChange={(e) => updateNestedField('features.landingPageBuilder', e.target.checked)}
                                                    />
                                                    <span>Landing Page Builder</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className={styles.featureSection}>
                                            <h3><i className="fas fa-code"></i> Advanced Features</h3>
                                            <div className={styles.featureGrid}>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.apiAccess}
                                                        onChange={(e) => updateNestedField('features.apiAccess', e.target.checked)}
                                                    />
                                                    <span>API Access</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.webhooks}
                                                        onChange={(e) => updateNestedField('features.webhooks', e.target.checked)}
                                                    />
                                                    <span>Webhooks</span>
                                                </label>
                                                <label className={styles.featureItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.customDevelopment}
                                                        onChange={(e) => updateNestedField('features.customDevelopment', e.target.checked)}
                                                    />
                                                    <span>Custom Development</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Settings Tab */}
                                {activeTab === 'settings' && (
                                    <div className={styles.tabContent}>
                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>Trial Duration (days)</label>
                                                <input
                                                    type="number"
                                                    value={formData.settings.trialDuration}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        updateNestedField('settings.trialDuration', val === '' ? '' : parseInt(val) || 0);
                                                    }}
                                                    onBlur={(e) => {
                                                        if (e.target.value === '' || e.target.value === '0') {
                                                            updateNestedField('settings.trialDuration', 7);
                                                        }
                                                    }}
                                                    min="0"
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Grace Period (days)</label>
                                                <input
                                                    type="number"
                                                    value={formData.settings.gracePeriod}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        // Allow empty string during editing, convert to number otherwise
                                                        updateNestedField('settings.gracePeriod', val === '' ? '' : parseInt(val) || 0);
                                                    }}
                                                    onBlur={(e) => {
                                                        // Set to default 7 if empty on blur
                                                        if (e.target.value === '' || e.target.value === '0') {
                                                            updateNestedField('settings.gracePeriod', 7);
                                                        }
                                                    }}
                                                    min="0"
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Minimum Commitment (months)</label>
                                                <input
                                                    type="number"
                                                    value={formData.settings.minimumCommitment}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        updateNestedField('settings.minimumCommitment', val === '' ? '' : parseInt(val) || 0);
                                                    }}
                                                    onBlur={(e) => {
                                                        if (e.target.value === '') {
                                                            updateNestedField('settings.minimumCommitment', 0);
                                                        }
                                                    }}
                                                    min="0"
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Refund Policy</label>
                                            <select
                                                value={formData.settings.refundPolicy}
                                                onChange={(e) => updateNestedField('settings.refundPolicy', e.target.value)}
                                            >
                                                <option value="none">No Refund</option>
                                                <option value="partial">Partial Refund</option>
                                                <option value="full">Full Refund</option>
                                            </select>
                                        </div>

                                        <div className={styles.formRow}>
                                            <div className={styles.formGroup}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.settings.allowDowngrade}
                                                        onChange={(e) => updateNestedField('settings.allowDowngrade', e.target.checked)}
                                                    />
                                                    Allow Downgrade
                                                </label>
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.settings.allowUpgrade}
                                                        onChange={(e) => updateNestedField('settings.allowUpgrade', e.target.checked)}
                                                    />
                                                    Allow Upgrade
                                                </label>
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.settings.autoRenewal}
                                                        onChange={(e) => updateNestedField('settings.autoRenewal', e.target.checked)}
                                                    />
                                                    Auto Renewal
                                                </label>
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.settings.proRateOnUpgrade}
                                                        onChange={(e) => updateNestedField('settings.proRateOnUpgrade', e.target.checked)}
                                                    />
                                                    Pro-rate on Upgrade
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.modalActions}>
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className={styles.cancelBtn}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.submitBtn}
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin"></i>
                                                {editingPackage ? 'Updating...' : 'Creating...'}
                                            </>
                                        ) : (
                                            <>
                                                {editingPackage ? 'Update' : 'Create'} Package
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default Packages;
